/**
 * 3-Spalten-Mono-Grid: [Zeit] [Tag] [Message].
 * - Zeit: --ink-400 (presenter) / --paper-400 (light)
 * - Tag: --accent-blue-soft, uppercase 11px tracking-meta
 * - Message: --ink-200 / --paper-500
 * Letzte Zeile blinkender Cursor in --accent-blue.
 */

export interface FeedEvent {
  id?: string;
  /** Unix-Millis oder formatierter String "14:32:08". */
  ts: number | string;
  tag: string;
  message: string;
  /** Optional: Highlight als "ok" → Tag/Message in Weiß-bold. */
  ok?: boolean;
}

interface EventFeedProps {
  events: FeedEvent[];
  /** Live-Cursor in der letzten Zeile? */
  cursor?: boolean;
  skin?: "presenter" | "light";
  /** Max anzuzeigende Events (neueste zuerst). */
  limit?: number;
}

function formatTs(ts: number | string): string {
  if (typeof ts === "string") return ts;
  const d = new Date(ts);
  return d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function EventFeed({ events, cursor, skin, limit }: EventFeedProps) {
  const slice = limit ? events.slice(-limit) : events;

  const tsColor = skin === "light" ? "var(--paper-400)" : "var(--ink-400)";
  const msgColor = skin === "light" ? "var(--paper-500)" : "var(--ink-200)";

  return (
    <ol
      className="beacon-eventfeed mono"
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "grid",
        gridTemplateColumns: "auto auto 1fr",
        columnGap: "var(--s-4)",
        rowGap: "var(--s-2)",
        fontFamily: "var(--font-mono)",
        fontSize: 14,
      }}
    >
      {slice.map((ev, idx) => {
        const isLast = idx === slice.length - 1;
        const okStyle: React.CSSProperties = ev.ok
          ? { color: "var(--svo-weiss, #fff)", fontWeight: 600 }
          : {};
        return (
          <li
            key={ev.id ?? `${idx}-${ev.tag}`}
            style={{ display: "contents" }}
          >
            <span style={{ color: tsColor }}>{formatTs(ev.ts)}</span>
            <span
              style={{
                color: ev.ok
                  ? "var(--svo-weiss, #fff)"
                  : "var(--accent-blue-soft)",
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: "var(--tracking-meta)",
                fontWeight: 500,
                alignSelf: "baseline",
              }}
            >
              {ev.tag}
            </span>
            <span style={{ color: msgColor, ...okStyle }}>
              {ev.message}
              {cursor && isLast && (
                <span
                  className="beacon-cursor"
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    marginLeft: 4,
                    color: "var(--accent-blue)",
                    animation: "beacon-blink 1.05s steps(2) infinite",
                  }}
                >
                  ▍
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
